import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@core-ui/Components/ui/card';
import { User2, Briefcase, Mail, Phone, Building2, Copy, Check } from 'lucide-react';
import clsx from 'clsx';
import FuegoLead from '@ui/shared/Dialer/FuegoLead.tsx';
import useWizardStore from '../../../features/wizard/store/useWizardStore.ts';
import { HubspotLogoTyped, SalesforceLogoTyped } from '@assets/index.ts';

interface ContactCardProps {
  id: string;
  externalId: string;
  name: string;
  jobTitle: string;
  email: string;
  phone: string;
  account: string;
  fuegoLead?: boolean;
  onFuegoLeadChange?: (value: boolean) => void;
  showFuegoLead: boolean;
  isTransferred: boolean;
  activeVoicemail: boolean;
  isSDR: boolean;
  sdrName?: string;
}

const ContactCard: React.FC<ContactCardProps> = ({
  id,
  externalId,
  name,
  jobTitle,
  email,
  phone,
  account,
  fuegoLead,
  onFuegoLeadChange,
  showFuegoLead,
  isTransferred,
  activeVoicemail,
  isSDR,
  sdrName,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const { salesforceURL, currentContactsTab, hubspotPortalId } = useWizardStore();
  const hasSalesforce = salesforceURL !== '' && currentContactsTab === 'salesforce';
  const hasHubspot = hubspotPortalId !== '' && currentContactsTab === 'hubspot';
  
  const handleCopy = useCallback(async () => {
    const textArray = [
      `${t('dialer.contact.name', 'Name')}: ${name !== '' ? name : '—'}`,
      `${t('dialer.contact.jobTitle', 'Job Title')}: ${jobTitle !== '' ? jobTitle : '—'}`,
      `${t('dialer.contact.email', 'Email')}: ${email !== '' ? email : '—'}`,
      `${t('dialer.contact.phone', 'Phone')}: ${phone !== '' ? phone : '—'}`,
      `${t('dialer.contact.account', 'Account')}: ${account !== '' ? account : '—'}`,
    ];

    if (isTransferred && !isSDR) {
      textArray.push(`${t('dialer.contact.transferredTo', 'Transferred to')}: ${sdrName !== '' ? sdrName : '—'}`);
    }
    
    const text = textArray.join('\n');

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        window.setTimeout(() => {
          setCopied(false);
        }, 1500);
      } finally {
        document.body.removeChild(ta);
      }
    }
  }, [name, jobTitle, email, phone, account, t, isTransferred, sdrName]);

  const externalLink = useMemo(() => {

    if (hasSalesforce) {
      return {
        url: `${salesforceURL}/lightning/r/Contact/${externalId}/view`,
        windowName: 'salesforceWindow',
        logo: SalesforceLogoTyped,
        alt: 'Salesforce icon',
      };
    }

    if (hasHubspot) {
      return {
        url: `https://app.hubspot.com/contacts/${hubspotPortalId}/record/0-1/${externalId}`,
        windowName: 'hubspotWindow',
        logo: HubspotLogoTyped,
        alt: 'HubSpot icon',
      };
    }

    return null;
  }, [hasSalesforce, hasHubspot, salesforceURL, externalId, hubspotPortalId]);

  const handleOpenExternalContact = useCallback(() => {
    if (externalLink === null) {
      return;
    }

    window.open( externalLink.url, externalLink.windowName, 'width=900,height=700,scrollbars=yes,resizable=yes');
  }, [externalLink]);

  return (
    <Card className={clsx(
      'bg-card rounded-2xl shadow-md border border-border md:col-span-3 relative min-h-[170px]',
      isTransferred && 'border-(--ring)',
      activeVoicemail && 'border-(--purple)'
    )}>
      <div className="absolute right-3 top-3">
        <button
          type="button"
          onClick={() => {
            void handleCopy();
          }}
          aria-label={t('dialer.contact.copyContact', 'Copy contact')}
          title={t('dialer.contact.copyContact', 'Copy contact')}
          className={clsx(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs md:text-sm font-medium transition-all duration-200 cursor-pointer',
            copied
              ? 'bg-green-500/15 border-green-500/40 text-green-600 dark:text-green-400'
              : 'bg-transparent border-border text-muted-foreground hover:border-primary/40 hover:text-primary'
          )}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? t('dialer.contact.copied', 'Copied!') : t('dialer.contact.copy', 'Copy')}
        </button>
      </div>

      <CardContent className="grid md:grid-cols-6 px-4 md:px-6 lg:px-8 text-foreground gap-y-1.5 md:gap-y-2 gap-x-4 md:gap-x-6 lg:gap-x-8 py-2">
        {/* Name */}
        <div className="flex items-center gap-2 md:col-span-2 min-w-0">

          <User2 className="size-4 md:size-5 shrink-0 text-primary" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs md:text-sm text-muted-foreground">
              {t('dialer.contact.name', 'Name')}
            </span>

            {isSDR && (hasSalesforce || hasHubspot) ? (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleOpenExternalContact();
                }}
                className="
                    inline-flex items-center gap-2 min-w-0
                    font-semibold text-base md:text-lg lg:text-xl
                    text-blue-300 hover:text-blue-500
                    hover:underline
                "
              >
                <span className="truncate">{name}</span>
                <img src={externalLink?.logo} alt={externalLink?.alt} className="w-10 h-10"/>
              </a>
            ) : (
              <span className="font-semibold text-base md:text-lg lg:text-xl truncate">{name}</span>
            )
            }
          </div>
        </div>

        {/* Job Title */}
        <div className="flex items-center gap-2 md:col-span-2 min-w-0">
          <Briefcase className="size-4 md:size-5 shrink-0 text-primary" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs md:text-sm text-muted-foreground">
              {t('dialer.contact.jobTitle', 'Job Title')}
            </span>
            <span className="font-semibold text-base md:text-lg lg:text-xl truncate">{jobTitle}</span>
          </div>
        </div>

        {/* Email */}
        <div className="flex items-center gap-2 md:col-span-2 min-w-0">
          <Mail className="size-4 md:size-5 shrink-0 text-primary" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs md:text-sm text-muted-foreground">
              {t('dialer.contact.email', 'Email')}
            </span>
            <span className="font-semibold text-base md:text-lg lg:text-xl truncate">{email}</span>
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-center gap-2 md:col-span-2 min-w-0">
          <Phone className="size-4 md:size-5 shrink-0 text-primary" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs md:text-sm text-muted-foreground">
              {t('dialer.contact.phone', 'Phone')}
            </span>
            <span className="font-semibold text-base md:text-lg lg:text-xl truncate">{phone}</span>
          </div>
        </div>

        {/* Account */}
        <div className="flex items-center gap-2 md:col-span-2 min-w-0">
          <Building2 className="size-4 md:size-5 shrink-0 text-primary" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs md:text-sm text-muted-foreground">
              {t('dialer.contact.account', 'Account')}
            </span>
            <span className="font-semibold text-base md:text-lg lg:text-xl truncate">{account}</span>
          </div>
        </div>

        {/* Fuego Lead */}
        {showFuegoLead && (
          <FuegoLead
            contactId={id}
            contactName={name}
            fuegoLead={fuegoLead ?? false}
            onFuegoLeadChange={onFuegoLeadChange}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ContactCard;
